/**
 * This file contains logic required to load Authorize.net's Accept.js into the app.
 *
 * This is modeled very similarly as Stripe's @stripe/stripe-js package, which achieves the same goal:
 * It acts solely as a loader for the internal Stripe SDK as Stripe's SDK is not open-source.
 * A key difference is that Accept.js does not to be initialized with identifiers. We pass the clientKeys and apiLoginId
 * when we are ready to begin the confirmation flow. This should help in understanding the loading mechanism.
 *
 * @see https://github.com/stripe/stripe-js/blob/b906dc16ef6476810de96c3a02019035aaea1907/src/shared.ts
 *
 */
/* eslint-disable no-console */
/* eslint-disable prefer-spread */
/* eslint-disable no-useless-return */
/* eslint-disable no-underscore-dangle */

import { logAppInfo, recordAppError } from "@shared/utils/errors/errorLogger";
import { AcceptJs } from "./types";

export interface LoadParams {
  advancedFraudSignals: boolean;
}

declare global {
  interface Window {
    // Accept.js must be loaded directly from https://js.authorize.net/v1/Accept.js, which
    // places an `Accept` object on the window
    Accept?: AcceptJs;
  }
}

const SANDBOX_URL = "https://jstest.authorize.net/v1/Accept.js";
const SANDBOX_URL_REGEX =
  /^https:\/\/jstest\.authorize\.net\/v1\/Accept\.js\/?(\?.*)?$/;

const PROD_URL = "https://js.authorize.net/v1/Accept.js";
const PROD_URL_REGEX =
  /^https:\/\/js\.authorize\.net\/v1\/Accept\.js\/?(\?.*)?$/;

const URL = process.env.APP_ENV === "production" ? PROD_URL : SANDBOX_URL;
const URL_REGEX =
  process.env.APP_ENV === "production" ? PROD_URL_REGEX : SANDBOX_URL_REGEX;

const EXISTING_SCRIPT_MESSAGE =
  "loadScript was called but an existing Accept.js script already exists in the document; existing script parameters will be used";

/**
 * Responsible for finding an existing Accept.js script on the DOM
 */
export const findScript = (): HTMLScriptElement | null => {
  const scripts = document.querySelectorAll<HTMLScriptElement>(
    `script[src^="${URL}"]`
  );

  for (let i = 0; i < scripts.length; i += 1) {
    const script = scripts[i];

    if (!URL_REGEX.test(script.src)) {
      // eslint-disable-next-line no-continue
      continue;
    }

    return script;
  }

  return null;
};

/**
 * Responsible for injecting the Accept.js script onto the DOM
 */
const injectScript = (params: null | LoadParams): HTMLScriptElement => {
  const queryString = params || "";
  const script = document.createElement("script");
  script.src = `${URL}${queryString}`;

  const headOrBody = document.head || document.body;

  if (!headOrBody) {
    throw new Error(
      "Expected document.body not to be null. Accept.js requires a <body> element."
    );
  }

  headOrBody.appendChild(script);

  return script;
};

let acceptPromise: Promise<AcceptJs | null> | null = null;

let onErrorListener: (() => void) | null = null;
let onLoadListener: (() => void) | null = null;

const onError = (reject: (reason?: unknown) => void) => () => {
  const error = new Error("Failed to load Accept.js of Authorize.net");
  logAppInfo("Accept.js script failed to load", {
    category: "AuthorizeLoader",
    data: {
      url: URL,
      error: "Script load error - check network tab for details",
    },
  });
  recordAppError(error);
  reject(error);
};

const onLoad =
  (
    resolve: (value: AcceptJs | PromiseLike<AcceptJs | null> | null) => void,
    reject: (reason?: unknown) => void
  ) =>
  () => {
    logAppInfo("Accept.js script loaded, checking window.Accept", {
      category: "AuthorizeLoader",
    });

    if (window.Accept) {
      logAppInfo("window.Accept is available", {
        category: "AuthorizeLoader",
        data: {
          hasDispatchData: typeof window.Accept.dispatchData === "function",
        },
      });
      resolve(window.Accept);
    } else {
      const error = new Error("Accept.js not available");
      logAppInfo("Script loaded but window.Accept unavailable", {
        category: "AuthorizeLoader",
        data: {
          error: "Script failed to initialize properly",
        },
      });
      recordAppError(error);
      reject(error);
    }
  };

/**
 * Manages loading the Accept.js SDK onto the DOM, from finding existing scripts to attaching event listeners.
 */
export const loadScript = async (
  params: null | LoadParams
): Promise<AcceptJs | null> => {
  // Ensure that we only attempt to load Accept.js at most once
  if (acceptPromise !== null) {
    return acceptPromise;
  }

  acceptPromise = new Promise((resolve, reject) => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      // Resolve to null when imported server side. This makes the module
      // safe to import in an isomorphic code base.
      logAppInfo("Server-side environment detected", {
        category: "AuthorizeLoader",
      });
      resolve(null);
      return;
    }

    logAppInfo("Starting Accept.js load process", {
      category: "AuthorizeLoader",
      data: {
        url: URL,
        environment: process.env.APP_ENV,
        existingAccept: !!window.Accept,
      },
    });

    if (window.Accept && params) {
      console.warn(EXISTING_SCRIPT_MESSAGE);
    }

    if (window.Accept) {
      logAppInfo("window.Accept already exists, using existing instance", {
        category: "AuthorizeLoader",
      });
      resolve(window.Accept);
      return;
    }

    try {
      let script = findScript();

      if (script && params) {
        console.warn(EXISTING_SCRIPT_MESSAGE);
      } else if (!script) {
        logAppInfo("No existing script found, injecting new script", {
          category: "AuthorizeLoader",
        });
        script = injectScript(params);
        logAppInfo("Script injected into DOM", {
          category: "AuthorizeLoader",
          data: {
            src: script.src,
            parentElement: script.parentElement?.tagName,
          },
        });
      } else if (
        script &&
        onLoadListener !== null &&
        onErrorListener !== null
      ) {
        logAppInfo("Existing script found with listeners, reloading", {
          category: "AuthorizeLoader",
        });
        // remove event listeners
        script.removeEventListener("load", onLoadListener);
        script.removeEventListener("error", onErrorListener);

        // if script exists, but we are reloading due to an error,
        // reload script to trigger 'load' event
        script.parentNode?.removeChild(script);
        script = injectScript(params);
        logAppInfo("Script reloaded", {
          category: "AuthorizeLoader",
        });
      }

      onLoadListener = onLoad(resolve, reject);
      script.addEventListener("load", onLoadListener);

      onErrorListener = onError(reject);
      script.addEventListener("error", onErrorListener);

      logAppInfo("Event listeners attached, waiting for script to load", {
        category: "AuthorizeLoader",
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      logAppInfo("Error during script loading setup", {
        category: "AuthorizeLoader",
        data: {
          error: errorMessage,
        },
      });
      recordAppError(error instanceof Error ? error : new Error(String(error)));
      reject(error);
      return;
    }
  });
  // Resets acceptPromise on error
  return acceptPromise.catch((error) => {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logAppInfo("Failed to load Accept.js", {
      category: "AuthorizeLoader",
      data: {
        error: errorMessage,
      },
    });
    acceptPromise = null;
    return Promise.reject(error);
  });
};
